import {
  BILLING_POINTS,
  COMPARE_TABLE,
  PRICING_INTRO,
  PRICING_NOTE,
  TIERS,
  TRIAL_COPY,
  TRIAL_MAIL_HEADING,
  TRIAL_MAIL_SUBJECT_PREFIX,
} from '@/content/pricing';
import { TRIAL_FIELDS } from '@/content/forms';
import { BRAND } from '@/content/site';
import { ActionButton } from '@/components/ActionButton';
import { Badge } from '@/components/Badge';
import { Callout, Card, CheckList } from '@/components/Card';
import { CopyField } from '@/components/CopyField';
import { Icon } from '@/components/Icon';
import { MailForm } from '@/components/MailForm';
import { Reveal } from '@/components/Reveal';
import { PageHero, Section, SectionHeading } from '@/components/Section';
import { Seo } from '@/components/Seo';

type CellValue = string;

function CompareCell({ value }: { value: CellValue }) {
  if (value === 'yes') {
    return (
      <span className="relative inline-flex items-center gap-1.5 text-fg">
        <Icon name="check" size={15} className="text-fg-muted" />
        <span className="sr-only">包含</span>
      </span>
    );
  }
  if (value === 'no') {
    return (
      <span className="relative inline-flex items-center gap-1.5 text-fg-subtle">
        <Icon name="close" size={14} />
        <span className="sr-only">不包含</span>
      </span>
    );
  }
  return <span className="text-xs text-fg-muted">{value}</span>;
}

export function PricingPage() {
  return (
    <>
      <Seo
        path="/pricing"
        crumbs={[
          { name: '首页', path: '/' },
          { name: '方案与定价', path: '/pricing' },
        ]}
      />

      <PageHero
        eyebrow={PRICING_INTRO.eyebrow}
        title={PRICING_INTRO.title}
        lead={PRICING_INTRO.lead}
        meta={<span>价格看机体数量、装在哪、要改多少</span>}
        actions={
          <>
            <ActionButton action={{ label: '申请试用', to: '#trial', variant: 'primary' }} />
            <ActionButton action={{ label: '看平台能力', to: '/platform', variant: 'secondary' }} />
          </>
        }
      />

      <Section>
        <div className="grid gap-5 lg:grid-cols-3">
          {TIERS.map((tier, index) => (
            <Reveal key={tier.id} delay={index} as="div">
              <Card
                interactive
                className={[
                  'flex h-full flex-col gap-5',
                  tier.highlighted ? 'border-line-strong shadow-[var(--shadow-lift)]' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h2 className="text-lg font-semibold text-fg">{tier.name}</h2>
                    <p className="mt-1 text-sm text-fg-muted">{tier.tagline}</p>
                  </div>
                  {tier.highlighted ? <Badge tone="solid">最常用</Badge> : null}
                </div>

                <div className="border-y border-line py-4">
                  <p className="metric-value text-2xl text-fg">{tier.price}</p>
                  <p className="mt-1 text-xs text-fg-muted">{tier.priceNote}</p>
                </div>

                <p className="text-xs leading-relaxed text-fg-muted">
                  <span className="text-fg-subtle">适用：</span>
                  {tier.audience}
                </p>

                <CheckList items={tier.features} />

                {tier.excluded ? (
                  <ul className="space-y-2 border-t border-line pt-4">
                    {tier.excluded.map((item) => (
                      <li key={item} className="flex gap-2.5 text-xs leading-relaxed text-fg-subtle">
                        <Icon name="close" size={13} className="mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : null}

                <div className="mt-auto pt-2">
                  <ActionButton action={tier.action} />
                </div>
              </Card>
            </Reveal>
          ))}
        </div>

        <div className="mt-6 max-w-3xl">
          <Callout label="关于整机" icon="info">
            {PRICING_NOTE}
          </Callout>
        </div>
      </Section>

      <Section tone="raised" id="compare">
        <SectionHeading
          eyebrow="能力对比"
          title="三档方案的差异所在"
          lead="基础版已覆盖完整的监管链路；往上两档增加的是操作记录保留时长、策略是否分层，以及系统的部署归属。"
        />

        <div className="mt-10 overflow-hidden rounded-xl border border-line bg-surface-1">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[38rem] border-collapse text-sm">
              <thead>
                <tr className="border-b border-line bg-grad-soft">
                  {COMPARE_TABLE.head.map((cell, index) => (
                    <th
                      key={cell}
                      scope="col"
                      className={[
                        'px-4 py-3 text-xs font-semibold text-fg',
                        index === 0 ? 'text-left' : 'text-center',
                      ].join(' ')}
                    >
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_TABLE.rows.map((row) => (
                  <tr key={row[0]} className="border-b border-line last:border-b-0">
                    <th scope="row" className="px-4 py-3 text-left text-sm font-normal text-fg-muted">
                      {row[0]}
                    </th>
                    {row.slice(1).map((cell, index) => (
                      <td key={index} className="px-4 py-3 text-center align-middle">
                        <CompareCell value={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          {BILLING_POINTS.map((point) => (
            <Card key={point.title} className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-fg">{point.title}</h3>
              <p className="text-xs leading-relaxed text-fg-muted">{point.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="soft" id="trial">
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1fr)_22rem] lg:items-start">
          <div>
            <SectionHeading
              eyebrow={TRIAL_COPY.eyebrow}
              title={TRIAL_COPY.title}
              lead={TRIAL_COPY.text}
              className="mb-8"
            />
            <MailForm
              fields={TRIAL_FIELDS}
              heading={TRIAL_MAIL_HEADING}
              subjectPrefix={TRIAL_MAIL_SUBJECT_PREFIX}
              submitLabel={TRIAL_COPY.submitLabel}
            />
          </div>

          <div className="flex flex-col gap-4">
            <CopyField label="直接发邮件" value={BRAND.email} href={`mailto:${BRAND.email}`} />
            <div className="rounded-xl border border-line bg-surface-1 p-4">
              <h3 className="text-sm font-semibold text-fg">我们会怎么回复</h3>
              <ul className="mt-3 space-y-2.5 text-xs leading-relaxed text-fg-muted">
                <li>依贵方机体规模与使用方式，判断应从哪一档起步。</li>
                <li>需要接入自研机体时，我们提供接口文档与联调步骤。</li>
                <li>涉及私有化部署或定制的，先问清楚边界再报价。</li>
              </ul>
              <p className="mt-4 border-t border-line pt-3 text-xs text-fg-subtle">{TRIAL_COPY.privacyNote}</p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
}
